/**
 * PDF export with pdfmake (MIT), loaded on demand (ADR 0018). Embedded Noto Sans (SIL OFL 1.1, Latin
 * subset with Turkish letters: public/fonts). A4 landscape, branded cover header, key figures and chart
 * (UI phase D, ADR 0031), the map snapshot when given, tables with repeated header rows, footer and page
 * numbers. A PDF is for reading: each table is capped at PDF_ROW_CAP rows and says so.
 */
import type { Content, TDocumentDefinitions, TableCell } from "pdfmake/interfaces";
import { chartSvg } from "../chart";
import { PDF_ROW_CAP, displayCell, fill, formatDateTimeIn, type CellType, type ExportMeta, type Sheet } from "./sheet";

type PdfMake = {
  createPdf: (
    dd: TDocumentDefinitions,
    tableLayouts?: unknown,
    fonts?: Record<string, Record<string, string>>,
    vfs?: Record<string, string>,
  ) => { getBuffer: (cb: (buf: Uint8Array) => void) => void };
};

async function loadPdfMake(): Promise<PdfMake> {
  const mod = (await import("pdfmake/build/pdfmake")) as unknown as PdfMake & { default?: PdfMake };
  return mod.default && typeof mod.default.createPdf === "function" ? mod.default : mod;
}

/** Base64 of a binary buffer, without Node's Buffer (browser, worker, Node ≥ 16). */
export function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

const FONT = "NotoSans";

/** PDF colours (pdfmake cannot read CSS; the values follow tokens.css). */
const INK = "#191919";
const MUTED = "#5e6163";
const LINE = "#e2e2df";
const HEAD_FILL = "#f4f4f2";
const ZEBRA = "#fafaf8";
const ACCENT = "#f96915";
/** Usable width of an A4 landscape page with the margins below (pt). */
const CONTENT_W = 841.89 - 2 * 32;

/**
 * Characters the embedded Noto Sans subset has no glyph for, written the way a reader expects
 * ("≥ 15 km/sa" → ">= 15 km/sa"); otherwise they would print as blanks.
 */
const PDF_TEXT: [RegExp, string][] = [[/≥/g, ">="], [/≤/g, "<="], [/▲/g, "+"], [/▼/g, "-"]];
export function pdfSafe(s: string): string {
  let out = s;
  for (const [re, to] of PDF_TEXT) out = out.replace(re, to);
  return out;
}

function safeChart(c: NonNullable<ExportMeta["chart"]>): NonNullable<ExportMeta["chart"]> {
  return {
    ...c,
    title: pdfSafe(c.title),
    spec: { ...c.spec, series: c.spec.series.map((x) => ({ ...x, label: pdfSafe(x.label) })), bars: c.spec.bars.map((b) => ({ ...b, label: pdfSafe(b.label) })) },
  };
}

/** Rows of a horizontal chart in the PDF: one page at most (the full list is in the table). */
export const PDF_CHART_MAX_ROWS = 25;

const groupInt = (n: number, locale: ExportMeta["locale"]) => new Intl.NumberFormat(locale === "tr" ? "tr-TR" : "en-GB").format(n);

const numericType = (t: CellType) => t === "int" || t === "dec1" || t === "dec2" || t === "percent" || t === "duration" || t === "coord";

/**
 * The PDF (UI phase D, ADR 0031): a cover header on the first page (Expartise logo, product, report title,
 * period, filters, generated at, generated by — the role), the key figures, the primary chart, then the
 * tables with repeated header rows. Every page after the first carries a short running header; every
 * page the footer "Expartise Makine · Forklift Takip Sistemi" and the page number.
 */
export function pdfDefinition(rawSheets: Sheet[], rawMeta: ExportMeta, mapImage?: string | null, compress = true, logo?: string | null): TDocumentDefinitions {
  const content: Content[] = [];
  const meta: ExportMeta = {
    ...rawMeta,
    filters: rawMeta.filters.map(([k, v]) => [pdfSafe(k), pdfSafe(v)]),
    summary: rawMeta.summary?.map((x) => ({ ...x, label: pdfSafe(x.label), value: pdfSafe(x.value) })),
    chart: rawMeta.chart ? safeChart(rawMeta.chart) : undefined,
  };
  const sheets = rawSheets.map((x) => ({ ...x, columns: x.columns.map((c) => ({ ...c, header: pdfSafe(c.header) })) }));
  const title = `FTS — ${meta.reportName}`;
  const L = meta.labels;

  // Cover header: logo left; product, report title and period right.
  content.push({
    columns: [
      logo ? { image: logo, width: 120 } : { text: "Expartise Makine", bold: true, fontSize: 14, width: 120 },
      {
        width: "*",
        stack: [
          ...(L.product ? [{ text: L.product, color: MUTED, fontSize: 9 }] : []),
          { text: meta.reportName, fontSize: 18, bold: true, color: INK, margin: [0, 2, 0, 0] },
          ...(meta.period ? [{ text: meta.period, fontSize: 10, color: INK, margin: [0, 2, 0, 0] }] : []),
          ...(meta.sample && L.sample ? [{ text: L.sample, fontSize: 9, bold: true, color: "#8f5600", margin: [0, 3, 0, 0] }] : []),
        ],
        margin: [16, 0, 0, 0],
      },
    ],
    columnGap: 0,
    margin: [0, 0, 0, 8],
  } as Content);
  content.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: CONTENT_W, y2: 0, lineWidth: 2, lineColor: ACCENT }], margin: [0, 0, 0, 8] } as Content);

  // Figures shown as summary boxes are not repeated among the filters (XLSX keeps them in its info sheet).
  const inSummary = new Set((meta.summary ?? []).map((s) => s.label));
  // The period is in the cover header; the raw date range is not repeated (XLSX keeps it).
  if (meta.period && L.dates) inSummary.add(L.dates);
  const infoRows: TableCell[][] = [
    ...meta.filters.filter(([k]) => !inSummary.has(k)).map(([k, v]) => [{ text: k, color: MUTED }, v] as TableCell[]),
    [{ text: L.timeZone, color: MUTED }, meta.tz],
    [{ text: L.generatedAt, color: MUTED }, formatDateTimeIn(meta.generatedAt, meta.tz, meta.locale)],
    [{ text: L.generatedBy, color: MUTED }, meta.generatedByRole ?? meta.generatedBy],
  ];
  content.push({ table: { widths: [130, "*"], body: infoRows }, layout: "noBorders", fontSize: 8, margin: [0, 0, 0, 10] });

  // Key figures: one row of boxes.
  const capped = sheets[0] && sheets[0].rows.length > PDF_ROW_CAP ? sheets[0] : null;
  if (meta.summary?.length) {
    const cells: TableCell[] = meta.summary.map((s) => ({
      stack: [
        { text: s.label, color: MUTED, fontSize: 8 },
        { text: s.value, bold: true, fontSize: 13, color: INK, margin: [0, 2, 0, 0] },
        ...(s.note ? [{ text: s.note, color: MUTED, fontSize: 7, margin: [0, 2, 0, 0] }] : []),
      ],
      fillColor: ZEBRA,
      margin: [6, 5, 6, 5],
    }) as TableCell);
    content.push({
      table: { widths: meta.summary.map(() => "*"), body: [cells], dontBreakRows: true },
      layout: { hLineColor: () => LINE, vLineColor: () => LINE, hLineWidth: () => 0.6, vLineWidth: () => 0.6 },
      margin: [0, 0, 0, capped ? 4 : 12],
    } as Content);
    // The figures cover every row the export returned; say so when the table below is cut.
    if (capped && L.summaryCapped) {
      content.push({ text: fill(L.summaryCapped, { total: groupInt(capped.rows.length, meta.locale), shown: groupInt(PDF_ROW_CAP, meta.locale) }), italics: true, color: MUTED, fontSize: 7.5, margin: [0, 0, 0, 12] });
    }
  }

  if (meta.chart && meta.chart.spec.bars.length > 0) {
    const all = meta.chart.spec.bars;
    const cut = meta.chart.spec.orientation === "horizontal" && all.length > PDF_CHART_MAX_ROWS;
    const spec = cut ? { ...meta.chart.spec, bars: all.slice(0, PDF_CHART_MAX_ROWS) } : meta.chart.spec;
    content.push({
      stack: [
        { text: meta.chart.title, bold: true, fontSize: 10, margin: [0, 0, 0, 4] },
        { svg: chartSvg(spec, CONTENT_W) },
        ...(cut && L.chartCap ? [{ text: fill(L.chartCap, { shown: PDF_CHART_MAX_ROWS, total: groupInt(all.length, meta.locale) }), italics: true, color: MUTED, fontSize: 7, margin: [0, 3, 0, 0] }] : []),
        ...(meta.chart.note ? [{ text: meta.chart.note, color: MUTED, fontSize: 7, margin: [0, 3, 0, 0] }] : []),
      ],
      unbreakable: true,
      margin: [0, 0, 0, 12],
    } as Content);
  }

  if (mapImage) content.push({ image: mapImage, fit: [CONTENT_W, 300], alignment: "center", margin: [0, 0, 0, 10] });
  else if (mapImage === null) content.push({ text: L.mapUnavailable, italics: true, color: MUTED, margin: [0, 0, 0, 10] });

  for (const s of sheets) {
    if (sheets.length > 1) content.push({ text: s.name, fontSize: 11, bold: true, margin: [0, 6, 0, 4] });
    if (s.rows.length === 0) {
      content.push({ text: L.noRows, italics: true, margin: [0, 0, 0, 8] });
      continue;
    }
    const rows = s.rows.slice(0, PDF_ROW_CAP);
    const body: TableCell[][] = [
      s.columns.map((c) => ({ text: c.header, bold: true, fillColor: HEAD_FILL, alignment: numericType(c.type) ? ("right" as const) : undefined })),
      ...rows.map((r) =>
        s.columns.map((c, j) => {
          const text = pdfSafe(displayCell(r[j] ?? null, c.type, meta));
          return numericType(c.type) ? { text, alignment: "right" as const } : text;
        }),
      ),
    ];
    content.push({
      table: { headerRows: 1, keepWithHeaderRows: 1, widths: s.columns.map(() => "auto"), body, dontBreakRows: true },
      layout: {
        hLineWidth: (i: number) => (i === 1 ? 0.8 : 0.4),
        vLineWidth: () => 0,
        hLineColor: (i: number) => (i === 1 ? MUTED : LINE),
        fillColor: (i: number) => (i > 0 && i % 2 === 0 ? ZEBRA : null),
        paddingTop: () => 2.5,
        paddingBottom: () => 2.5,
      },
      fontSize: 7,
      margin: [0, 0, 0, 8],
    } as Content);
    if (s.rows.length > rows.length) {
      content.push({ text: fill(L.pdfRowCap, { shown: rows.length, total: s.rows.length }), italics: true, margin: [0, 0, 0, 8] });
    }
  }

  const running = [meta.reportName, meta.period].filter(Boolean).join(" · ");
  return {
    pageSize: "A4",
    pageOrientation: "landscape",
    compress,
    pageMargins: [32, 40, 32, 36],
    info: { title, creator: "FTS", producer: "FTS" },
    // The first page has the cover header; the others a running header. Called without a page number
    // (e.g. by tools that only read the definition) it returns the running header.
    header: (page?: number) =>
      page === 1
        ? null
        : {
            columns: [
              { text: `FTS — ${running}`, bold: true, fontSize: 8 },
              { text: formatDateTimeIn(meta.generatedAt, meta.tz, meta.locale), alignment: "right", fontSize: 8, color: MUTED },
            ],
            margin: [32, 16, 32, 0],
          },
    footer: (page: number, pages: number) => ({
      columns: [
        { text: L.footer ?? "", fontSize: 7.5, color: MUTED },
        { text: fill(L.page, { page, pages }), alignment: "right", fontSize: 7.5, color: MUTED },
      ],
      margin: [32, 12, 32, 0],
    }),
    content,
    defaultStyle: { font: FONT, fontSize: 8, color: INK },
  } as TDocumentDefinitions;
}

export async function buildPdf(
  sheets: Sheet[],
  meta: ExportMeta,
  fonts: { regular: ArrayBuffer; bold: ArrayBuffer },
  mapImage?: string | null,
  opts: { compress?: boolean; logo?: string | null } = {},
): Promise<Uint8Array> {
  const pdfMake = await loadPdfMake();
  const vfs = { "NotoSans-Regular.ttf": toBase64(fonts.regular), "NotoSans-Bold.ttf": toBase64(fonts.bold) };
  const fontDefs = {
    [FONT]: { normal: "NotoSans-Regular.ttf", bold: "NotoSans-Bold.ttf", italics: "NotoSans-Regular.ttf", bolditalics: "NotoSans-Bold.ttf" },
  };
  const doc = pdfMake.createPdf(pdfDefinition(sheets, meta, mapImage, opts.compress ?? true, opts.logo), undefined, fontDefs, vfs);
  return new Promise<Uint8Array>((resolve, reject) => {
    try {
      doc.getBuffer((b) => resolve(b));
    } catch (e) {
      reject(e);
    }
  });
}
